import supabase from '../supabase/supabase';

/*
 * =========================================================
 * HELPERS
 * =========================================================
 */

const clean = (value) =>
  typeof value === 'string' ? value.trim() : '';

const getAuthenticatedUser = async () => {
  const { data, error } = await supabase.auth.getUser();

  if (error || !data?.user) {
    throw new Error(
      'Your session has expired. Please sign in again.'
    );
  }

  return data.user;
};

const getRows = (value) => {
  if (Array.isArray(value)) return value;
  return value && typeof value === 'object' ? [value] : [];
};

const optionalNumber = (value) => {
  if (value === '' || value == null) return null;

  const number = Number(value);

  if (!Number.isFinite(number) || !Number.isInteger(number)) {
    throw new Error('Please enter a valid whole number.');
  }

  return number;
};

const optionalBoolean = (value) =>
  value === true || value === 'true';

const getOrganizationId = (row) =>
  row?.organization_id ||
  row?.org_id ||
  row?.id ||
  null;

const getManagingFirmId = (row) =>
  row?.managing_organization_id ||
  row?.managing_org_id ||
  row?.account_id ||
  null;

const getFirmWorkspaces = async () => {
  await getAuthenticatedUser();

  const { data, error } = await supabase.rpc(
    'get_my_firm_workspaces'
  );

  if (error) {
    throw new Error(
      `Unable to load firm memberships: ${error.message}`
    );
  }

  return getRows(data)
    .filter((row) =>
      Boolean(row.organization_id && row.membership_role)
    )
    .map((row) => ({
      id: row.organization_id,
      name: row.organization_name,
      type: row.organization_type,
      role: row.membership_role
    }));
};

const getSingleFirm = async ({ requireOwner = false } = {}) => {
  const firms = await getFirmWorkspaces();

  if (firms.length === 0) {
    throw new Error(
      'No verified managing firm workspace was found.'
    );
  }

  if (firms.length > 1) {
    throw new Error(
      'Multiple firm memberships were found. ' +
      'An explicitly selected firm is required for this operation.'
    );
  }

  const firm = firms[0];

  if (requireOwner && firm.role !== 'owner') {
    throw new Error(
      'Only a verified firm owner can perform this operation.'
    );
  }

  return firm;
};

const getAuthorizedClientRows = async () => {
  const { data, error } = await supabase.rpc(
    'get_client_hub_organizations_v2'
  );

  if (error) {
    throw new Error(
      `Unable to verify client access: ${error.message}`
    );
  }

  return getRows(data);
};

/*
 * =========================================================
 * SETTINGS SERVICE
 * =========================================================
 */

export const settingsService = {
  /*
   * -------------------------------------------------------
   * CREATE CLIENT ORGANIZATION
   * -------------------------------------------------------
   */
  createAccount: async (accountData, userId) => {
    try {
      const user = await getAuthenticatedUser();

      if (userId && userId !== user.id) {
        throw new Error(
          'The user does not match the authenticated session.'
        );
      }

      const firm = await getSingleFirm({
        requireOwner: true
      });

      const name = clean(accountData?.name);

      if (name.length < 2 || name.length > 120) {
        throw new Error(
          'Business name must contain between 2 and 120 characters.'
        );
      }

      /*
       * Matches the already tested Supabase RPC.
       * No country, currency or fiscal-year defaults.
       * The database resolves missing settings
       * from the verified managing firm.
       */
      const payload = {
        p_name: name,
        p_industry: clean(accountData?.industry),
        p_entity_type: clean(accountData?.entity_type),
        p_country: clean(accountData?.country),
        p_currency: clean(accountData?.currency).toUpperCase(),
        p_fiscal_year: clean(accountData?.fiscal_year_end),
        p_year_started: optionalNumber(
          accountData?.year_started
        ),
        p_tax_id: clean(accountData?.tax_id),
        p_monthly_revenue: clean(accountData?.monthly_revenue),
        p_employee_count: clean(accountData?.employee_count),
        p_accepts_online_payments: optionalBoolean(
          accountData?.accepts_online_payments
        ),
        p_bookkeeping_manager: clean(
          accountData?.bookkeeping_manager
        ),
        p_uses_software: optionalBoolean(
          accountData?.uses_software
        ),
        p_business_address: clean(accountData?.address),
        p_business_email: clean(accountData?.email),
        p_phone: clean(accountData?.phone),
        p_website: clean(accountData?.website)
      };

      const { data, error } = await supabase.rpc(
        'create_client_organization',
        payload
      );

      if (error) throw error;

      const created = getRows(data)[0];
      const createdId = getOrganizationId(created);

      /*
       * Creation RPC should return:
       * organization_id, account_id, name, type.
       *
       * A missing or mismatched response must
       * never trigger automatic client switching.
       */
      if (!createdId) {
        return {
          success: true,
          verified: false,
          warning:
            'The creation request completed without a client ID. ' +
            'Check your client list before submitting again.',
          data: null
        };
      }

      const returnedFirmId = getManagingFirmId(created);

      if (returnedFirmId !== firm.id) {
        return {
          success: true,
          verified: false,
          warning:
            'The creation response did not confirm the expected ' +
            'managing firm. Check the organization relationship ' +
            'before using this client.',
          data: {
            id: createdId,
            name: created.name || name,
            account_id: returnedFirmId,
            type: created.type || null
          }
        };
      }

      /*
       * Check whether the new client is available
       * through the authenticated user's client hub.
       *
       * Note: matching the managing firm requires
       * that the hub RPC exposes its firm relationship.
       * Missing relationship fields do not count
       * as verification.
       */
      let verifiedClient = null;
      let verificationWarning = '';

      try {
        const rows = await getAuthorizedClientRows();

        const matched = rows.find((row) => {
          const isClient =
            row.is_client === true ||
            row.isClient === true;

          return (
            getOrganizationId(row) === createdId &&
            isClient &&
            row.type === 'client'
          );
        });

        if (
          matched &&
          getManagingFirmId(matched) === firm.id
        ) {
          verifiedClient = matched;
        } else {
          verificationWarning =
            'The client was created, but its access and managing ' +
            'firm could not yet be confirmed in the client hub. ' +
            'Check the Clients page before submitting again.';
        }
      } catch (verifyError) {
        console.error(
          'Client creation verification failed:',
          verifyError
        );

        verificationWarning =
          'The client creation request succeeded, but the ' +
          'client list could not be verified. Check the Clients page.';
      }

      return {
        success: true,
        verified: Boolean(verifiedClient),
        warning: verificationWarning,
        data: {
          id: createdId,
          account_id: firm.id,
          name: verifiedClient?.name || created.name || name,
          type: created.type || 'client',
          status: verifiedClient?.status || null
        }
      };
    } catch (error) {
      console.error(
        'settingsService.createAccount:',
        error
      );

      return {
        success: false,
        verified: false,
        error: error.message || 'Unable to create client.'
      };
    }
  },

  /*
   * -------------------------------------------------------
   * CREATE BUSINESS ALIAS
   * -------------------------------------------------------
   */
  createBusiness: async (accountData) => {
    const user = await getAuthenticatedUser();

    const response = await settingsService.createAccount(
      accountData,
      user.id
    );

    if (!response.success) {
      throw new Error(
        response.error || 'Unable to create business.'
      );
    }

    return response.data;
  },

  /*
   * -------------------------------------------------------
   * UPDATE USER PROFILE
   * -------------------------------------------------------
   */
  updateUserProfile: async (profileData) => {
    const user = await getAuthenticatedUser();

    const { data, error } = await supabase
      .from('profiles')
      .update({
        first_name: clean(profileData.first_name),
        last_name: clean(profileData.last_name),
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;

    return data;
  },

  /*
   * -------------------------------------------------------
   * EXISTING TWO-STEP REGISTRATION
   *
   * Retained for compatibility.
   * Its database contract has not yet been audited.
   * -------------------------------------------------------
   */
  completeTwoStepRegistration: async (registrationData) => {
    await getAuthenticatedUser();

    const workspaceName = clean(
      registrationData.workspaceName
    );

    if (!workspaceName) {
      throw new Error('Workspace name is required.');
    }

    const workspaceSlug =
      clean(registrationData.workspaceSlug) ||
      workspaceName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

    const { data, error } = await supabase.rpc(
      'complete_two_step_registration',
      {
        p_workspace_name: workspaceName,
        p_workspace_slug: workspaceSlug,
        p_first_name: clean(registrationData.firstName),
        p_last_name: clean(registrationData.lastName),
        p_phone: clean(registrationData.phone)
      }
    );

    if (error) throw error;

    return { id: data };
  },

  /*
   * -------------------------------------------------------
   * LEGACY ONBOARDING GUARD
   *
   * The current registration workflow provisions
   * firm workspaces during signup.
   * -------------------------------------------------------
   */
  completePlatformOnboarding: async () => {
    throw new Error(
      'This onboarding operation is not enabled for the ' +
      'current organization model.'
    );
  },

  /*
   * -------------------------------------------------------
   * GET VERIFIED FIRM
   * -------------------------------------------------------
   */
  getCompanyDetails: async () => {
    const firm = await getSingleFirm();

    const { data, error } = await supabase
      .schema('accounting')
      .from('organizations')
      .select('*')
      .eq('id', firm.id)
      .single();

    if (error) throw error;

    return {
      ...data,
      membership_role: firm.role
    };
  },

  /*
   * -------------------------------------------------------
   * UPDATE VERIFIED FIRM
   * -------------------------------------------------------
   */
  updateCompanyDetails: async (accountData) => {
    const firm = await getSingleFirm({
      requireOwner: true
    });

    const updates = {};

    if (
      accountData.name !== undefined ||
      accountData.legal_name !== undefined
    ) {
      updates.name = clean(
        accountData.name ?? accountData.legal_name
      );

      if (!updates.name) {
        throw new Error('Firm name cannot be empty.');
      }
    }

    if (accountData.website !== undefined) {
      updates.website = clean(accountData.website);
    }

    if (accountData.fiscal_year_end !== undefined) {
      updates.fiscal_year = clean(
        accountData.fiscal_year_end
      );
    }

    if (accountData.currency !== undefined) {
      updates.currency = clean(
        accountData.currency
      ).toUpperCase();
    }

    if (accountData.country !== undefined) {
      updates.country = clean(accountData.country);
    }

    if (Object.keys(updates).length === 0) {
      throw new Error('No changes were provided.');
    }

    updates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .schema('accounting')
      .from('organizations')
      .update(updates)
      .eq('id', firm.id)
      .select()
      .single();

    if (error) throw error;

    return data;
  },

  /*
   * -------------------------------------------------------
   * GET CURRENT USER ORGANIZATIONS
   * -------------------------------------------------------
   */
  getAccounts: async () => {
    const user = await getAuthenticatedUser();

    const { data, error } = await supabase
      .schema('accounting')
      .from('organization_users')
      .select(`
        organization_id,
        role,
        organizations (
          id,
          name,
          type,
          country,
          currency,
          fiscal_year,
          website,
          created_at
        )
      `)
      .eq('user_id', user.id);

    if (error) throw error;

    return (data || [])
      .filter((membership) =>
        Boolean(membership.organizations)
      )
      .map((membership) => ({
        ...membership.organizations,
        role: membership.role
      }));
  },

  /*
   * -------------------------------------------------------
   * TEAM MEMBERS
   * -------------------------------------------------------
   */
  getUsers: async () => {
    await getAuthenticatedUser();

    const { data, error } = await supabase.rpc(
      'get_my_team_members'
    );

    if (error) throw error;

    return (data || []).map((member) => ({
      id: member.user_id,
      user_id: member.user_id,
      email: member.email || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      full_name: member.full_name || '',
      role: member.role || null,
      account_id: member.account_id || null,
      created_at: member.created_at || null,
      status: member.status || null
    }));
  },

  updateUserRole: async (userId, role) => {
    await getAuthenticatedUser();

    const { data, error } = await supabase.rpc(
      'update_team_member_role',
      {
        target_user_id: userId,
        new_role: role
      }
    );

    if (error) throw error;

    return data;
  },

  /*
   * -------------------------------------------------------
   * UPDATE VERIFIED FIRM
   * -------------------------------------------------------
   */
  updateAccount: async (id, accountData) => {
    const firm = await getSingleFirm({
      requireOwner: true
    });

    if (id !== firm.id) {
      throw new Error(
        'This operation can only update your verified firm.'
      );
    }

    const allowedFields = [
      'name',
      'country',
      'currency',
      'fiscal_year',
      'website'
    ];

    const updates = {};

    for (const field of allowedFields) {
      if (accountData[field] !== undefined) {
        updates[field] = accountData[field];
      }
    }

    if (Object.keys(updates).length === 0) {
      throw new Error('No supported fields were supplied.');
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from('organizations')
      .update(updates)
      .eq('id', firm.id)
      .select()
      .single();

    if (error) throw error;

    return data;
  },

  /*
   * -------------------------------------------------------
   * GET ORGANIZATION BY ID
   * Supabase RLS controls record visibility.
   * -------------------------------------------------------
   */
  getAccountById: async (id) => {
    await getAuthenticatedUser();

    if (!id) {
      throw new Error('Organization ID is required.');
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return data;
  },

  /*
   * -------------------------------------------------------
   * VERIFIED FIRM WORKSPACES
   * -------------------------------------------------------
   */
  getFirmWorkspaces: async () => {
    return getFirmWorkspaces();
  }
};